import React, {Component} from "react";
import {connect} from "react-redux";
import {injectIntl} from "react-intl";
import {isMobile} from "react-device-detect";
import dayjs from "dayjs";
import {GSTAR_UGC_PID_LIST, IPX_PID, REDBRICK_EVENT_PID, STORAGE_KEY, ADMIN_ACCOUNTS} from "utils";
import * as TrackingUtil from "utils/TrackingUtil";
import {viewWorld, getProject, getProjectTemplateList, createWorldMonitoring, updateWorldMonitoring, getUser} from "api-v3";
import * as tutorialActions from "store/actions/tutorial";
import {changePlayOption} from "store/actions/playOption";
import {editChatMode} from "store/actions/chat";
import {getTemplates} from "store/actions/template";
import {showPopUp, TwoBtnPopUp} from "components/PopUp";
import ExitPopup from "components/IPXContents/Common/ExitPopup";
import View from "./View";
import InformationPopup from "components/PopUp/InformationPopup";

class Container extends Component {
  constructor() {
    super();
    this.queryParams = new URLSearchParams(window.location.search);
    this.isDemoApp = this.queryParams.get("isDemoApp");
    this.pId = this.queryParams.get("pid");
    this.isGuest = this.queryParams.get("isGuest");

    if (this.queryParams.get("mode") === "play") {
      this.isEditMode = false;
    } else {
      this.isEditMode = true;
    }

    if (GSTAR_UGC_PID_LIST.includes(this.pId)) {
      this.isGstarUgcMode = true;
    }
    for (const [key, value] of Object.entries(IPX_PID)) {
      if (value === this.pId) {
        this.isIPXMode = true;
        this.ipxProjectKey = key;
      }
    }
    for (const [key, value] of Object.entries(REDBRICK_EVENT_PID)) {
      if (value === this.pId) {
        this.isRedbrickEventMode = true;
        this.redbrickEventProjectKey = key;
      }
    }
    this.state = {
      isVerticalMode: window.innerHeight > window.innerWidth,
      pId: this.pId,
      template: this.queryParams.get("template"),
    };

    this.startTime = dayjs();
  }

  componentDidMount() {
    this.handleClickFullScreen();
    this.detectOrientation();
    this.getTemplatesID();
    if (window.location.hostname !== "localhost") {
      this.checkMyOwnProject();
      this.startWorldMonitoring();
      this.checkWorldMonitoring();
      this.updateWorldViewCount();
    }
    window.addEventListener("beforeunload", this.sendPageViewEvent);
    window.addEventListener("resize", () => {
      this.mobileFullScreen();
      this.detectOrientation();
    });
    window.addEventListener("touchend", this.mobileFullScreen);
    window.addEventListener("orientationchange", () => {
      this.mobileFullScreen();
      this.detectOrientation();
    });
    window.addEventListener("keydown", this.keyboardPlay);

    if (this.isEditMode) {
      const startDate = new Date("2023-08-24");
      const endDate = new Date("2023-09-08");
      const currentDate = new Date();

      if (currentDate >= startDate && currentDate <= endDate) {
        showPopUp(<InformationPopup />, {defaultPadding: false, noBackground: true});
      }
    }
  }

  componentWillUnmount() {
    window.removeEventListener("beforeunload", this.sendPageViewEvent);
    this.openTutorialForLearnMode();
    window.removeEventListener("resize", () => {
      this.mobileFullScreen();
      this.detectOrientation();
    });
    window.removeEventListener("orientationchange", () => {
      this.mobileFullScreen();
      this.detectOrientation();
    });
    window.removeEventListener("touchend", this.mobileFullScreen);
    window.removeEventListener("keydown", this.keyboardPlay);
  }

  openTutorialForLearnMode() {
    const searchParams = new URLSearchParams(window.location.search);
    const currentMode = searchParams.get("mode");
    if (currentMode === "learn") this.props.handleTutorialPanel();
  }

  sendPageViewEvent = e => {
    e.preventDefault();
    const {userinfo} = this.props;
    if (userinfo.email) {
      TrackingUtil.sendAPEvent({
        event: "studioPageview",
        "DLV - pid": this.pId,
        "DLV - duration_time": TrackingUtil.getDurationTime(this.startTime),
        "DLV - type": "3D",
        "DLV - age": TrackingUtil.getAge(userinfo.birth),
        "DLV - gender": userinfo.gender,
        "DLV - live": TrackingUtil.isLiveUser(userinfo),
      });
    }
  };

  handleClickFullScreen = event => {
    let element = document.body;
    const isInDetailPlayPage = window.parent?.location.pathname === "/detail-play";

    if (event instanceof HTMLElement) {
      element = event;
    }

    let isFullscreen = document.webkitIsFullScreen || document.mozFullScreen || false;

    element.requestFullScreen =
      element.requestFullScreen ||
      element.webkitRequestFullScreen ||
      element.mozRequestFullScreen ||
      function () {
        return false;
      };
    document.cancelFullScreen =
      document.cancelFullScreen ||
      document.webkitCancelFullScreen ||
      document.mozCancelFullScreen ||
      function () {
        return false;
      };

    if (isInDetailPlayPage) return;
    isFullscreen ? document.cancelFullScreen() : element.requestFullScreen();
  };

  mobileFullScreen() {
    let vh = window.innerHeight * 0.01;
    document.documentElement.style.setProperty("--vh", `${vh}px`);
  }

  keyboardPlay = e => {
    if (this.isIPXMode) return;
    switch (e.code) {
      case "Enter":
        if (this.props.playOption === null) {
          this.props.changePlayOption("chat");
          this.props.editChatMode(false);
        }
        break;
      case "Escape":
        if (this.props.chatMode) {
          this.props.editChatMode(false);
        }
        if (this.props.playOption !== "avatar") {
          this.props.changePlayOption(null);
        }
        break;
      default:
        break;
    }
  };

  onClickExit = () => {
    if (this.isIPXMode === true) {
      showPopUp(
        <ExitPopup
          clickExit={() => {
            if (isMobile) {
              if (window.android) {
                window.android.finishWorld();
              }
              if (window.webkit) {
                window.webkit.messageHandlers.finishWorld.postMessage({});
              }
            }
          }}
        />,
        {
          defaultPadding: false,
          transparentBackground: true,
          clearOverHidden: true,
        },
      );
    } else {
      showPopUp(
        <TwoBtnPopUp
          title={this.props.intl.formatMessage({id: "ID_DEMO_PLAY_EXIT"})}
          confirmBtn={this.props.intl.formatMessage({
            id: "ID_DEMO_PLAY_EXIT_CONFIRM",
          })}
          cancelBtn={this.props.intl.formatMessage({
            id: "ID_DEMO_PLAY_EXIT_CANCEL",
          })}
          confirmAction={() => {
            if (isMobile) {
              if (window.android) {
                window.android.finishWorld();
              }
              if (window.webkit) {
                window.webkit.messageHandlers.finishWorld.postMessage({});
              }
            } else {
              window.location.href = `${window.location.protocol}//${window.location.host}/demo-app`;
            }
          }}
        />,
        {
          defaultPadding: false,
        },
      );
    }
  };

  detectOrientation = () => {
    if (window.innerHeight > window.innerWidth) {
      this.setState({
        isVerticalMode: true,
      });
    } else {
      this.setState({
        isVerticalMode: false,
      });
    }
  };

  checkMyOwnProject = async () => {
    const openWorldLink = () => {
      window.open(`${window.location.protocol}//${window.location.host}/studio/?mode=play&pid=${this.state.pId}`, "_self");
    };

    if (this.isEditMode && !this.state.template) {
      try {
        const res = await getProject({id: this.state.pId});
        const resUser = await getUser();
        if (ADMIN_ACCOUNTS.includes(resUser?.data?.email)) return;
        if (res?.data.authorCi !== resUser?.data?.ci) {
          openWorldLink();
        }
      } catch (e) {
        openWorldLink();
      }
    }
  };

  getTemplatesID = async () => {
    if (this.isGuest) return;
    const response = await getProjectTemplateList({platform: "REDBRICK", offset: 0});
    this.props.getTemplates(response.data.items);
  };

  getUserOs = () => {
    let userOs = navigator.userAgent.replace(/ /g, "").toLowerCase();

    if (userOs.indexOf("macintosh") !== -1) {
      return "mac";
    } else if (userOs.indexOf("window") !== -1) {
      return "window";
    } else if (userOs.indexOf("android") !== -1) {
      return "android";
    } else if (userOs.indexOf("iphone") !== -1) {
      return "ios";
    } else if (userOs.indexOf("ipad") !== -1) {
      return "ipad";
    } else {
      return "unknown";
    }
  };

  updateWorldViewCount = () => {
    viewWorld({id: this.state.pId});
  };

  startWorldMonitoring = async () => {
    const os = this.getUserOs();
    const response = await createWorldMonitoring({pid: this.state.pId, os: os, entryAt: Date.now()});
    if (response.code === 1) {
      localStorage.setItem("monitorId", response.data.id);
    }
  };

  checkWorldMonitoring = () => {
    const monitorInterval = setInterval(() => {
      const monitorId = localStorage.getItem("monitorId");

      if (monitorId) {
        updateWorldMonitoring({id: monitorId}, {lastResponsedAt: Date.now()});
      }
    }, 60000);
  };

  render() {
    return (
      <View
        isEditMode={this.isEditMode}
        isLearnMode={this.isLearnMode}
        isCodeEditorOpen={this.props.codeEditor.isOpen}
        openedPopup={this.props.popup}
        isSelfieMode={this.props.selfie.isActivated}
        isIPXMode={this.isIPXMode}
        isRedbrickEventMode={this.isRedbrickEventMode}
        ipxProjectKey={this.ipxProjectKey}
        redbrickEventProjectKey={this.redbrickEventProjectKey}
        isGstarUgcMode={this.isGstarUgcMode}
        isDemoApp={this.isDemoApp}
        isVerticalMode={this.state.isVerticalMode}
        onClickExit={this.onClickExit}
      />
    );
  }
}

export default connect(
  state => ({
    codeEditor: state.codeEditor,
    userinfo: state.userinfo,
    selfie: state.selfie,
    playOption: state.playOption,
    chatMode: state.chat.chatMode,
    templates: state.template.templates,
  }),
  {
    handleTutorialPanel: tutorialActions.handleTutorialPanel,
    changePlayOption,
    editChatMode,
    getTemplates,
  },
)(injectIntl(Container));
